import { useMemo, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { usePortfolio } from '../../hooks/usePortfolio';

const PortfolioPage = () => {
  const { portfolioItems, loading } = usePortfolio();
  const [selectedCategory, setSelectedCategory] = useState('Todos');

  const categories = useMemo(() => {
    const dynamicCategories = portfolioItems
      .map((item: any) => item.category)
      .filter(Boolean);

    return ['Todos', ...Array.from(new Set(dynamicCategories))];
  }, [portfolioItems]);

  const filteredItems = useMemo(() => {
    if (selectedCategory === 'Todos') return portfolioItems;
    return portfolioItems.filter((item: any) => item.category === selectedCategory);
  }, [portfolioItems, selectedCategory]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p>Carregando portfólio...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <WhatsAppButton />

      <section className="pt-32 pb-16 bg-gradient-to-br from-primary to-primary-dark text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <span className="text-accent-yellow font-poppins text-sm mb-4 block">(trabalhos realizados)</span>
            <h1 className="font-montserrat font-extrabold text-5xl md:text-6xl mb-6">
              Nosso Portfólio
            </h1>
            <p className="text-xl font-poppins text-white/90 max-w-3xl mx-auto">
              Conheça alguns dos projetos que realizamos com excelência e dedicação.
            </p>
          </div>
        </div>
      </section>

      <section className="py-8 bg-gray-50 sticky top-20 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-poppins font-medium transition-all whitespace-nowrap border ${
                  selectedCategory === category
                    ? 'bg-accent-red text-white border-accent-red'
                    : 'bg-white text-gray-700 hover:bg-accent-red hover:text-white border-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {filteredItems.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-600 text-lg">Nenhum item no portfólio ainda.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredItems.map((item: any) => (
                <article key={item.id} className="group cursor-pointer">
                  <div className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={item.image || ''}
                        alt={item.title || 'Projeto'}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      {item.category && (
                        <div className="absolute top-4 left-4">
                          <span className="bg-accent-yellow text-primary px-4 py-1 rounded-full text-sm font-poppins font-medium">
                            {item.category}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="p-6">
                      <h2 className="font-montserrat font-bold text-2xl text-primary mb-3">
                        {item.title || 'Sem título'}
                      </h2>

                      <p className="text-gray-600 font-poppins mb-4">
                        {item.description || ''}
                      </p>

                      <div className="flex flex-wrap gap-2">
                        {(item.tags || []).map((tag: string, index: number) => (
                          <span
                            key={index}
                            className="text-xs font-poppins text-gray-600 bg-gray-100 px-3 py-1 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="font-montserrat font-bold text-5xl md:text-6xl text-accent-yellow mb-2">500+</div>
              <p className="font-poppins text-white/90">Projetos Realizados</p>
            </div>
            <div className="text-center">
              <div className="font-montserrat font-bold text-5xl md:text-6xl text-accent-yellow mb-2">300+</div>
              <p className="font-poppins text-white/90">Clientes Satisfeitos</p>
            </div>
            <div className="text-center">
              <div className="font-montserrat font-bold text-5xl md:text-6xl text-accent-yellow mb-2">5</div>
              <p className="font-poppins text-white/90">Anos de Experiência</p>
            </div>
            <div className="text-center">
              <div className="font-montserrat font-bold text-5xl md:text-6xl text-accent-yellow mb-2">98%</div>
              <p className="font-poppins text-white/90">Satisfação</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-montserrat font-bold text-4xl md:text-5xl text-primary mb-6">
            Quer fazer parte do nosso portfólio?
          </h2>
          <p className="text-xl font-poppins text-gray-700 mb-8">
            Entre em contato e vamos criar algo incrível juntos!
          </p>
          <a
            href="https://wa.me/5521992215234?text=Olá!%20Gostaria%20de%20solicitar%20um%20orçamento%20da%20JR%20Gráfica%20e%20Sublimação."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-accent-red text-white px-10 py-5 rounded-full font-poppins font-semibold hover:bg-red-600 transition-all whitespace-nowrap shadow-xl text-lg"
          >
            <i className="ri-whatsapp-line text-2xl"></i>
            Solicitar Orçamento
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PortfolioPage;